import { useEffect, useMemo, useState } from 'react';
import { NavLink, useMatch, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, LayoutDashboard, MessageSquare, Settings, UserRound, Users } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../core/auth/AuthContext';
import { usePartnerPreferences } from '../contexts/PartnerPreferencesContext';

interface Partner {
  id: string;
  prefix?: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Patient {
  id: string;
  prefix?: string;
  firstName: string;
  lastName: string;
}

interface ConversationSummary {
  id: string;
  patient?: Patient | null;
  participants?: Partner[];
  lastMessageAt?: string;
  createdAt: string;
  unreadCount?: number;
}

function initials(firstName: string, lastName: string) {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

export function ZilloTeamSidebar() {
  const { user, account } = useAuth();
  const { t } = useTranslation('zilloteam');
  const [searchParams, setSearchParams] = useSearchParams();
  const { getDisplayName, getColorScheme, getAvatarUrl } = usePartnerPreferences();
  const isOnDiscussionsList = useMatch('/zilloteam/discussions');
  const isOnConversation = useMatch('/zilloteam/discussions/:conversationId');
  const isOnDiscussions = isOnDiscussionsList || isOnConversation;

  function resolveAvatarUrl(url: string | null): string | null {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
    const uploadsOrigin = (import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '');
    return `${uploadsOrigin}${url}`;
  }
  const [allConversations, setAllConversations] = useState<ConversationSummary[]>([]);

  const selectedPartners = useMemo(
    () => searchParams.get('partners')?.split(',').filter(Boolean) ?? [],
    [searchParams],
  );
  const selectedPatients = useMemo(
    () => searchParams.get('patients')?.split(',').filter(Boolean) ?? [],
    [searchParams],
  );

  const conversationMatch = useMatch('/zilloteam/discussions/:conversationId');
  const openConversationId = conversationMatch?.params.conversationId;

  useEffect(() => {
    api.getConversations().then((res) => {
      if (res.success && res.data) {
        setAllConversations(res.data.conversations as ConversationSummary[]);
      }
    });
  }, []);

  // When user opens a conversation, reset its unread count in the sidebar
  useEffect(() => {
    if (!openConversationId) return;
    setAllConversations((prev) =>
      prev.map((c) => (c.id === openConversationId ? { ...c, unreadCount: 0 } : c)),
    );
  }, [openConversationId]);

  // Unique partners (excluding self), ordered by appearance (most recent conv first)
  const allPartners = useMemo(() => {
    const seen = new Set<string>();
    const result: { partner: Partner; colorIndex: number }[] = [];
    for (const conv of allConversations) {
      for (const p of conv.participants ?? []) {
        if (p.id !== user?.id && !seen.has(p.id)) {
          seen.add(p.id);
          result.push({ partner: p, colorIndex: result.length });
        }
      }
    }
    return result;
  }, [allConversations, user?.id]);

  // Unique patients — cross-filtered by selected partners
  const visiblePatients = useMemo(() => {
    const convPool =
      selectedPartners.length > 0
        ? allConversations.filter((c) =>
            c.participants?.some((p) => selectedPartners.includes(p.id)),
          )
        : allConversations;
    const seen = new Set<string>();
    const result: Patient[] = [];
    for (const conv of convPool) {
      if (conv.patient && !seen.has(conv.patient.id)) {
        seen.add(conv.patient.id);
        result.push(conv.patient);
      }
    }
    return result;
  }, [allConversations, selectedPartners]);

  // Unique partners — cross-filtered by selected patients (only when no partner is selected)
  const visiblePartners = useMemo(() => {
    if (selectedPatients.length > 0 && selectedPartners.length === 0) {
      const convPool = allConversations.filter(
        (c) => c.patient && selectedPatients.includes(c.patient.id),
      );
      const partnerIds = new Set<string>();
      for (const conv of convPool) {
        for (const p of conv.participants ?? []) {
          if (p.id !== user?.id) partnerIds.add(p.id);
        }
      }
      return allPartners.filter(({ partner }) => partnerIds.has(partner.id));
    }
    return allPartners;
  }, [allConversations, allPartners, selectedPatients, selectedPartners, user?.id]);

  function toggleFilter(type: 'partners' | 'patients', id: string) {
    setSearchParams((prev) => {
      const current = prev.get(type)?.split(',').filter(Boolean) ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      const newParams = new URLSearchParams(prev);
      if (next.length > 0) {
        newParams.set(type, next.join(','));
      } else {
        newParams.delete(type);
      }

      // When partners change, cleanup patient selections that are no longer visible
      if (type === 'partners') {
        const convPool =
          next.length > 0
            ? allConversations.filter((c) =>
                c.participants?.some((p) => next.includes(p.id)),
              )
            : allConversations;
        const visiblePatientIds = new Set(
          convPool.flatMap((c) => (c.patient ? [c.patient.id] : [])),
        );
        const currentPatients = prev.get('patients')?.split(',').filter(Boolean) ?? [];
        const validPatients = currentPatients.filter((pid) => visiblePatientIds.has(pid));
        if (validPatients.length > 0) {
          newParams.set('patients', validPatients.join(','));
        } else {
          newParams.delete('patients');
        }
      }

      return newParams;
    });
  }

  const unreadConversationCount = useMemo(
    () => allConversations.filter((c) => (c.unreadCount ?? 0) > 0).length,
    [allConversations],
  );

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
      isActive ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
    }`;

  return (
    <aside className="w-60 h-full bg-slate-200 dark:bg-gray-800 text-gray-700 dark:text-white flex-shrink-0 flex flex-col overflow-hidden border-r border-gray-300 dark:border-gray-700">
      {/* Module Header */}
      <div
        className="px-4 py-4 border-b border-gray-300 dark:border-gray-700 flex-shrink-0"
        style={{ borderLeftColor: '#8b5cf6', borderLeftWidth: '3px' }}
      >
        <h2 className="font-semibold">ZilloTalk</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('sidebarZT.subtitle')}</p>
      </div>

      {/* Navigation */}
      <nav className="p-2 flex-shrink-0">
        <div className="mb-2">
          <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('sidebarZT.navigationTitle')}
          </h3>
          <ul className="space-y-1">
            <li>
              <NavLink to="/zilloteam" end className={navLinkClass}>
                <LayoutDashboard size={18} />
                <span>{t('sidebarZT.dashboard')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/zilloteam/discussions" className={navLinkClass}>
                <MessageSquare size={18} />
                <span className="flex-1">{t('sidebarZT.discussions')}</span>
                {unreadConversationCount > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-blue-500 text-white text-xs font-medium">
                    {unreadConversationCount > 99 ? '99+' : unreadConversationCount}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/zilloteam/patients" className={navLinkClass}>
                <UserRound size={18} />
                <span>{t('sidebarZT.patients')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/zilloteam/partenaires" className={navLinkClass}>
                <Users size={18} />
                <span>{t('sidebarZT.partners')}</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </nav>

      {isOnDiscussions && <>
      <div className="border-t border-gray-300 dark:border-gray-700 flex-shrink-0" />

      {/* Filter header */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0 text-center">
        <div className="inline-block px-3 py-0.5 rounded-full bg-gray-200 dark:bg-white/10 border border-gray-300 dark:border-white/20 mb-1">
          <p className="text-[11px] font-bold text-gray-700 dark:text-white tracking-widest uppercase">{t('sidebarZT.filterTitle')}</p>
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">{t('sidebarZT.filterHint')}</p>
      </div>

      {/* Partners filter */}
      <div className="flex-shrink-0">
        <div className="px-4 pb-1 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('sidebarZT.partners')}
          </h3>
          {selectedPartners.length > 0 && (
            <button
              onClick={() => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete('partners'); return p; })}
              className="text-[10px] text-blue-400 hover:text-blue-200 transition-colors font-medium"
            >
              {t('sidebarZT.deselectAll')}
            </button>
          )}
        </div>
        <div
          className="overflow-y-auto px-2 pb-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full"
          style={{ maxHeight: '228px' }}
        >
          {visiblePartners.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 italic">{t('sidebarZT.noPartner')}</p>
          ) : (
            visiblePartners.map(({ partner, colorIndex }) => {
              const colors = getColorScheme(partner.id, colorIndex);
              const isSelected = selectedPartners.includes(partner.id);
              const fallbackName = `${partner.prefix ? partner.prefix + ' ' : ''}${partner.firstName} ${partner.lastName}`;
              const avatarUrl = resolveAvatarUrl(getAvatarUrl(partner.id));
              return (
                <button
                  key={partner.id}
                  onClick={() => toggleFilter('partners', partner.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? 'bg-gray-200 dark:bg-white/15 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                    isSelected ? 'bg-blue-500 border-blue-400' : 'border-gray-300 dark:border-gray-500 bg-transparent'
                  }`}>
                    {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div
                      className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold ${colors.avatarBg} ${colors.avatarText}`}
                    >
                      {initials(partner.firstName, partner.lastName)}
                    </div>
                  )}
                  <span className="truncate text-left flex-1 text-sm">
                    {getDisplayName(partner.id, fallbackName)}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="border-t border-gray-300 dark:border-gray-700 flex-shrink-0" />

      {/* Patients filter */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-4 pt-3 pb-1 flex items-center justify-between flex-shrink-0">
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('sidebarZT.patients')}
          </h3>
          {selectedPatients.length > 0 && (
            <button
              onClick={() => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete('patients'); return p; })}
              className="text-[10px] text-blue-400 hover:text-blue-200 transition-colors font-medium"
            >
              {t('sidebarZT.deselectAll')}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
          {visiblePatients.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 italic">{t('sidebarZT.noPatient')}</p>
          ) : (
            visiblePatients.map((patient) => {
              const isSelected = selectedPatients.includes(patient.id);
              return (
                <button
                  key={patient.id}
                  onClick={() => toggleFilter('patients', patient.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? 'bg-gray-200 dark:bg-white/15 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                    isSelected ? 'bg-blue-500 border-blue-400' : 'border-gray-300 dark:border-gray-500 bg-transparent'
                  }`}>
                    {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold bg-primary-700 text-primary-200">
                    {initials(patient.firstName, patient.lastName)}
                  </div>
                  <span className="truncate text-left flex-1 text-sm">
                    {patient.prefix ? `${patient.prefix} ` : ''}
                    {patient.lastName} {patient.firstName}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      </>}

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-300 dark:border-gray-700">
        <NavLink
          to="/zilloteam/backup"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
              isActive
                ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                : 'text-gray-500 dark:text-gray-400 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
            }`
          }
        >
          <Settings size={16} />
          <span>{t('sidebarZT.settings')}</span>
        </NavLink>
        <div className="px-4 pb-3 pt-1">
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center truncate">{account?.name}</p>
        </div>
      </div>
    </aside>
  );
}

export default ZilloTeamSidebar;
